// The index sheet composer: one index sheet's values entered on its datasheet.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dsProjectApi, dynamicApi, databasesApi, appCfgApi } from '../../services/api';
import { activeDbName, urlDatabase } from '../../services/activeDatabase';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { FileDown, RefreshCw, Save } from 'lucide-react';
import { sheetLayout, sheetLayouts } from '../development/dsTemplateLayout';
import { isDsValues, isCalc, makeCalcEvaluator } from '../development/dsCalc';
import { fieldText, toFkFields, relatedLinkFor, boundTables, relatedId } from '../development/dsRelated';
import { useDsTemplates } from '../development/useDsTemplates';
import ActionMenu from '../../components/common/ActionMenu';
import exportDsPdf from '../development/dsPdfExporter';
import { dsErrMsg, toIndexSide, onSheet, linesToBoxes } from './dsBoxes';
import { DSPreview } from './DSPreview';
import StuiSelect from '../../components/common/StuiSelect';

// Every read and write the index composer makes, by what it does. `projectSource` reads the
// project database the X-Database-Id header names: its Index Sheets, the boxes of ITS copy of each
// datasheet (packages print the project's boxes) and its values. Another page can pass its own
// object with the same methods as the `source` prop. Each method returns the axios response, so
// callers read `.data` as before.
export const projectSource = {
  getDatabases:      ()                      => databasesApi.getAvailable(),
  getFieldMetadata:  (table)                 => appCfgApi.getFieldMetadata(table),
  getRelatedRow:     (table, id)             => dynamicApi.getById(table, id),
  getInstruments:    ()                      => dynamicApi.getAll('Index_Sheet'),
  getLayoutSpec:     ()                      => dsProjectApi.getLayoutSpec(),
  getLines:          (dsConfigId)            => dsProjectApi.getComposerLines(dsConfigId),
  getValues:         (instrumentId)          => dsProjectApi.getValues(instrumentId),
  getTags:           (indexSheet)            => dynamicApi.getAll('Tags', { search: indexSheet, limit: 10 }),
  updateValue:       (id, payload, config)   => dsProjectApi.updateValue(id, payload, config),
  createValue:       (payload, config)       => dsProjectApi.createValue(payload, config),
};

// onLabelClick, when given, is called with a label's text when it is clicked on the preview.
export function IndexComposer({ allDsIndex = [], source = projectSource, onLabelClick = undefined }) {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const auditCfg = { headers: { 'X-Changed-By': currentUser?.email || '' } };
  const [instruments,    setInstruments]    = useState([]);
  const [instrumentId,   setInstrumentId]   = useState(null);
  const [instrument,     setInstrument]     = useState(null);
  const [boxes,          setBoxes]          = useState([]);
  const [rawBoxes,       setRawBoxes]       = useState([]);
  const [activeSheet,    setActiveSheet]    = useState(1);
  // Which side of each box to display. A box carries two content values - a tag-based one and
  // an index-based one - and this page has always shown the index side. 'tag' is a VIEW only:
  // DSValues are per index sheet, so editing and saving stay on the index side regardless.
  const [side,           setSide]           = useState('idx');
  const [firstTag,       setFirstTag]       = useState(null);
  // FK fields on Tags / Index_Sheet (AppCfg_FieldMetadata), and the one row of each related
  // table the template binds, keyed by lower-cased table name. See dsRelated.js.
  const [fkFields,       setFkFields]       = useState([]);
  const [related,        setRelated]        = useState({});
  const [savedValues,    setSavedValues]    = useState({});
  const [draftValues,    setDraftValues]    = useState({});
  const [loadingInst,    setLoadingInst]    = useState(false);
  const [loadingData,    setLoadingData]    = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [databases,      setDatabases]      = useState([]);
  const [selectedDbName, setSelectedDbName] = useState(() => activeDbName());
  const [dbKey,          setDbKey]          = useState(0);

  // Load available databases for the selector
  useEffect(() => {
    source.getDatabases()
      .then(r => setDatabases(r.data || []))
      .catch(() => {});
  }, [source]);

  // On a data page the URL names the database, so choosing another one moves this tab there (same
  // page, new slug) and the reload below follows the URL. Never a quiet header swap: the rest of
  // the page would keep showing the old database.
  const navigate = useNavigate();
  const location = useLocation();
  const handleDbChange = (dbName) => {
    const db = databases.find(d => d.databaseName === dbName);
    if (db?.slug && urlDatabase().kind === 'slug') {
      navigate(location.pathname.replace(/^\/[^/]+/, `/${db.slug}`) + location.search);
      return;
    }
    setSelectedDbName(dbName);
    setInstrumentId(null);
    setDbKey(k => k + 1);
  };

  // Follow the tab's database when the slug changes.
  useEffect(() => {
    const current = activeDbName();
    if (!current || current === selectedDbName) return;
    setSelectedDbName(current);
    setInstrumentId(null);
    setDbKey(k => k + 1);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Field metadata lives in the admin DB and is shared by every project, so it loads once.
  useEffect(() => {
    Promise.all([source.getFieldMetadata('Tags'), source.getFieldMetadata('Index_Sheet')])
      .then(([t, i]) => setFkFields(toFkFields([...(t.data ?? []), ...(i.data ?? [])])))
      .catch(() => setFkFields([]));
  }, [source]);

  // One fetch per related table the template binds, for the row the tag (or index sheet)
  // points at. Tags.* and Index_Sheet.* need nothing extra. The dynamic API reads project
  // tables only, so a related table flagged FK_Admin_DB prints blank here; package snapshots
  // resolve it on the server.
  useEffect(() => {
    let cancelled = false;
    const links = boundTables(rawBoxes)
      .map(t => relatedLinkFor(t, fkFields))
      .filter(l => l && !l.adminDb);
    if (links.length === 0) { setRelated({}); return undefined; }
    Promise.all(links.map(link => {
      const id = relatedId(link, firstTag, instrument);
      if (id == null) return Promise.resolve([link, null]);
      return source.getRelatedRow(link.table, id)
        .then(r => [link, r.data ?? null])
        .catch(() => [link, null]);
    })).then(pairs => {
      if (cancelled) return;
      const next = {};
      for (const [link, row] of pairs) next[String(link.table).toLowerCase()] = row;
      setRelated(next);
    });
    return () => { cancelled = true; };
  }, [source, rawBoxes, fkFields, firstTag, instrument]);

  // Load instruments — reruns when selected DB changes (dbKey bump)
  useEffect(() => {
    setLoadingInst(true);
    source.getInstruments()
      .then(r => {
        const rows = Array.isArray(r.data) ? r.data : (r.data?.data ?? []);
        rows.sort((a, b) => (a.index_Sheet || '').localeCompare(b.index_Sheet || ''));
        setInstruments(rows);
        if (rows.length > 0) setInstrumentId(rows[0].id);
      })
      .catch(e => notify.error(dsErrMsg(e, 'Failed to load instruments.')))
      .finally(() => setLoadingInst(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, notify, dbKey]);

  // When instrument changes: find its DS template, load boxes + saved values + first tag.
  // Extracted so the 6-dot Refresh re-runs the same load rather than a partial one.
  const loadData = useCallback(() => {
    if (!instrumentId || instruments.length === 0) {
      setInstrument(null); setBoxes([]); setRawBoxes([]); setFirstTag(null);
      setSavedValues({}); setDraftValues({});
      return;
    }
    const inst = instruments.find(i => i.id === instrumentId) || null;
    setInstrument(inst);
    const dsConfigId = inst?.dS_Config;
    if (!dsConfigId) {
      setBoxes([]); setRawBoxes([]); setFirstTag(null); setSavedValues({}); setDraftValues({});
      return;
    }
    setLoadingData(true);
    Promise.all([
      source.getLines(dsConfigId),
      source.getValues(instrumentId),
      source.getTags(inst.index_Sheet),
    ])
      .then(([linesRes, valuesRes, tagsRes]) => {
        const raw = linesToBoxes(linesRes.data ?? []);
        const bxs = raw.map(toIndexSide);
        setRawBoxes(raw);
        setBoxes(bxs);
        setActiveSheet(1);
        const saved = {};
        for (const v of (valuesRes.data ?? [])) saved[v.ds_Line] = { id: v.id, value: v.ds_Line_Value ?? '' };
        setSavedValues(saved);
        const draft = {};
        for (const b of bxs.filter(b => isDsValues(b.fieldSource))) draft[b.id] = saved[b.id]?.value ?? '';
        setDraftValues(draft);
        const tagsData = Array.isArray(tagsRes.data) ? tagsRes.data : (tagsRes.data?.data ?? []);
        const match = tagsData.find(t => t.index_Sheet === instrumentId || t.indexSheet === instrumentId) || tagsData[0] || null;
        setFirstTag(match);
      })
      .catch(() => notify.error('Failed to load datasheet data.'))
      .finally(() => setLoadingData(false));
  }, [source, instrumentId, instruments, notify]);

  useEffect(() => { loadData(); }, [loadData]);

  // A field binding's value. dsvalues and calc: boxes are not fields and read blank here.
  const fieldValue = (box) => {
    const src = box.fieldSource;
    if (!src || isDsValues(src) || isCalc(src)) return '';
    const clean = src.startsWith('admin:') ? src.slice(6) : src;
    const dot = clean.indexOf('.');
    if (dot < 0) return '';
    const table = clean.slice(0, dot).toLowerCase();
    const field = clean.slice(dot + 1).toLowerCase();
    // An FK column holds an ID; the dynamic API returns its resolved text beside it as
    // {Field}_Display (AppCfg_FieldMetadata FK_Table / FK_Display_Field), and that is what the
    // sheet prints. It is what makes the second hop read as a name: the tag reaches its
    // Index_Sheet row, whose Manufacturer_Display is the manufacturer. A null display (dangling
    // FK) falls back to the raw value. Mirrors DsSnapshotBuilder.FieldText.
    if (table === 'index_sheet') return fieldText(instrument, field);
    if (table === 'tags')        return fieldText(firstTag, field);
    // Any other table is a related table: the row the tag or index sheet points at by FK,
    // loaded above. No link, or no row, prints blank.
    return fieldText(related[table], field);
  };

  // Calc boxes read their inputs from the whole template - an input may sit on another sheet -
  // using what is on screen right now: typed dsvalues from draftValues, fields as resolved. One
  // evaluator per side, because the two sides can bind a box differently. Mirrors the server's
  // two-pass DsSnapshotBuilder.ResolveAll.
  const evaluatorFor = (list) => makeCalcEvaluator(
    list,
    b => b.fieldSource,
    b => (isDsValues(b.fieldSource) ? (draftValues[b.id] ?? '') : fieldValue(b)),
  );
  const idxCalc  = evaluatorFor(boxes);
  const shownCalc = side === 'tag' ? evaluatorFor(rawBoxes) : idxCalc;

  const valueWith = (calc) => (box) => (isCalc(box.fieldSource) ? calc.value(box) : fieldValue(box));
  const resolveDataValue = valueWith(shownCalc);

  const dsvaluesBoxes = boxes.filter(b => isDsValues(b.fieldSource));
  const isDirty = dsvaluesBoxes.some(b => (draftValues[b.id] ?? '') !== (savedValues[b.id]?.value ?? ''));

  const handleSave = async () => {
    if (!instrumentId) return;
    setSaving(true);
    try {
      for (const box of dsvaluesBoxes) {
        const text     = draftValues[box.id] ?? '';
        const existing = savedValues[box.id];
        if (existing) {
          if (text !== existing.value) await source.updateValue(existing.id, { DS_Line: box.id, DS_Line_Value: text, Index_Sheet: instrumentId }, auditCfg);
        } else if (text.trim()) {
          const res = await source.createValue({ DS_Line: box.id, DS_Line_Value: text, Index_Sheet: instrumentId }, auditCfg);
          setSavedValues(prev => ({ ...prev, [box.id]: { id: res.data.id, value: text } }));
        }
      }
      setSavedValues(prev => {
        const updated = { ...prev };
        for (const box of dsvaluesBoxes) {
          const text = draftValues[box.id] ?? '';
          if (updated[box.id]) updated[box.id] = { ...updated[box.id], value: text };
        }
        return updated;
      });
      notify.success('Values saved.');
    } catch (err) {
      const detail = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'unknown error';
      notify.error(`Save failed: ${detail}`);
    } finally { setSaving(false); }
  };

  const dsName = instrument?.dS_Config_Display || (instrument?.dS_Config ? `DS #${instrument.dS_Config}` : null);

  // Page count comes from the boxes rather than the DSIndex row: this component only ever
  // loads the template's lines, and every box carries its own sheet, so there is nothing
  // extra to fetch and nothing that can disagree with what is on the canvas.
  const sheetCount = useMemo(
    () => Math.max(1, ...boxes.map(b => b.sheet ?? 1)),
    [boxes]
  );
  const sheetNumbers = useMemo(
    () => Array.from({ length: sheetCount }, (_, i) => i + 1),
    [sheetCount]
  );
  // The last page is Application Specific by definition; a single-page template has none.
  const sheetLabel = useCallback(
    n => (sheetCount > 1 && n === sheetCount ? 'Application Specific' : `Content ${n}`),
    [sheetCount]
  );
  const sheetBoxes = useMemo(
    () => (side === 'tag' ? rawBoxes : boxes).filter(b => onSheet(b, activeSheet)),
    [boxes, rawBoxes, side, activeSheet]
  );

  // The instrument's template type picks its layout spec; dS_Config is the UtlCfg_DSIndex id.
  const specRows = useDsTemplates(source.getLayoutSpec);
  const dsRecord = allDsIndex.find(d => d.id === instrument?.dS_Config) ?? null;
  const tempType = dsRecord?.ds_TempType ?? null;
  const formName = dsRecord?.ds_Name ?? null;
  const layout   = useMemo(
    () => sheetLayout(specRows, tempType, activeSheet, sheetCount, formName),
    [specRows, tempType, activeSheet, sheetCount, formName]
  );
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!instrumentId || boxes.length === 0) return;
    setExporting(true);
    try {
      // Fields and calc results; the PDF reads dsvalues from userValues instead.
      const resolveIdx = valueWith(idxCalc);
      const resolved = {};
      for (const box of boxes.filter(b => b.fieldSource && !isDsValues(b.fieldSource))) {
        resolved[box.id] = resolveIdx(box);
      }
      await exportDsPdf({
        boxes,
        resolvedValues: resolved,
        userValues:     draftValues,
        metadata:       {
          dsName: instrument?.index_Sheet || String(instrumentId),
          sheetLayouts: sheetLayouts(specRows, tempType, sheetCount, formName),
        },
      });
    } catch (err) {
      notify.error(`Export failed: ${err.message}`);
    } finally { setExporting(false); }
  };

  const indexActions = [
    { key: 'refresh', icon: <RefreshCw size={16} />, label: 'Refresh',
      hidden: !instrumentId,
      onClick: () => loadData() },
    { key: 'export', icon: <FileDown size={16} />, label: exporting ? 'Exporting…' : 'Download PDF',
      hidden: !instrumentId || loadingData || boxes.length === 0,
      onClick: handleExport },
    { key: 'save', icon: <Save size={16} />, label: saving ? 'Saving…' : 'Save',
      hidden: !instrumentId || loadingData || !isDirty,
      onClick: handleSave },
  ].filter(a => !a.hidden);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Instrument selector */}
      <div className="stui-page-header" style={{ gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
        <label className="stui-label" style={{ minWidth: 'unset' }}>Index Sheet</label>
        <StuiSelect className="stui-select" value={instrumentId ?? ''} onChange={e => setInstrumentId(parseInt(e.target.value, 10) || null)}
          disabled={loadingInst} style={{ minWidth: 240 }}>
          {loadingInst ? <option>Loading…</option>
            : instruments.map(i => <option key={i.id} value={i.id}>{i.index_Sheet}</option>)}
        </StuiSelect>
        <label className="stui-label" style={{ minWidth: 'unset' }}>Show</label>
        <StuiSelect className="stui-select" value={side} onChange={e => setSide(e.target.value)}
          style={{ minWidth: 130 }}
          title="Which side of each box to display. Values are saved per index sheet either way.">
          <option value="idx">Index based</option>
          <option value="tag">Tag based</option>
        </StuiSelect>
        {dsName    && <span className="stui-label-xs">Datasheet: <strong>{dsName}</strong></span>}

        {/* Same sheet strip as the Template Composer. Without it every sheet's lines ran into
            one sequential list and Application Specific fields appeared among the content. */}
        {sheetCount > 1 && (
          <div className="stui-module-header-tabs">
            {sheetNumbers.map(nSheet => (
              <button
                key={nSheet}
                className={`stui-dataentry-tab${activeSheet === nSheet ? ' active' : ''}`}
                onClick={() => setActiveSheet(nSheet)}
                title={sheetLabel(nSheet)}
              >
                <span>{sheetLabel(nSheet)}</span>
              </button>
            ))}
          </div>
        )}
        {firstTag  && <span className="stui-label-xs">Tag: <strong>{firstTag.tag_Number || firstTag.tagNumber || firstTag.id}</strong></span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <label className="stui-label" style={{ minWidth: 'unset' }}>Database Reference</label>
          <StuiSelect className="stui-select" value={selectedDbName} onChange={e => handleDbChange(e.target.value)} style={{ minWidth: 160 }}>
            {databases.map(db => (
              <option key={db.id ?? db.databaseName} value={db.databaseName}>{db.displayName || db.databaseName}</option>
            ))}
          </StuiSelect>
          {/* Same 6-dot treatment as the Template Composer - hidden, not disabled. */}
          <ActionMenu
            disabled={indexActions.length === 0}
            items={indexActions}
          />
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', justifyContent: 'center', paddingTop: 24, paddingBottom: 40, background: '#c8c8c8' }}>
        {loadingData ? (
          <div className="stui-empty-state">Loading…</div>
        ) : !instrument?.dS_Config ? (
          <div className="stui-empty-state" style={{ padding: 48 }}>
            {instrument ? 'No datasheet configured for this instrument.' : 'Select an instrument.'}
          </div>
        ) : boxes.length === 0 ? (
          <div className="stui-empty-state" style={{ padding: 48 }}>No fields configured in this template.</div>
        ) : (
          <DSPreview boxes={sheetBoxes} draftValues={draftValues} setDraftValues={setDraftValues} resolveDataValue={resolveDataValue} sheetCount={sheetCount} layout={layout} onLabelClick={onLabelClick} />
        )}
      </div>
    </div>
  );
}
